'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { EmblaOptionsType } from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useImperativeHandle } from 'react';
type AutoplayOptions = Parameters<typeof Autoplay>[0];
interface HorizontalScrollProps {
  children: ReactNode;
  className?: string;
  slideClassName?: string;
  /** 슬라이드 변경 시 콜백 */
  onSlideChange?: (index: number) => void;
  /** 스크롤 위치(0-1)와 콘텐츠 중 보이는 비율(0-1) 콜백 */
  onScrollMetrics?: (metrics: {
    progress: number;
    visibleRatio: number;
  }) => void;
  /** 무한 루프 활성화 */
  loop?: boolean;
  /** 자동 재생 옵션 */
  autoplay?: boolean | AutoplayOptions;
  autoScroll?: Parameters<typeof AutoScroll>[0];
  /** 슬라이드 정렬 */
  align?: EmblaOptionsType['align'];
  /** 드래그 자유도 */
  dragFree?: boolean;
  /** API 접근을 위한 ref */
  carouselRef?: React.RefObject<CarouselApi | null>;
}
export interface CarouselApi {
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number) => void;
  selectedScrollSnap: () => number;
  canScrollNext: () => boolean;
  canScrollPrev: () => boolean;
}
export const HorizontalScroll = t0 => {
  const $ = _c(44);
  const {
    children,
    className,
    slideClassName,
    onSlideChange,
    onScrollMetrics,
    loop: t1,
    autoplay: t2,
    autoScroll,
    align: t3,
    dragFree: t4,
    carouselRef
  } = t0;
  const loop = t1 === undefined ? false : t1;
  const autoplay = t2 === undefined ? false : t2;
  const align = t3 === undefined ? "start" : t3;
  const dragFree = t4 === undefined ? true : t4;
  const prefersReducedMotion = useReducedMotion();
  const t5 = autoScroll ? true : !loop && dragFree;
  const t6 = loop ? false : "trimSnaps";
  let t7;
  if ($[0] !== align || $[1] !== loop || $[2] !== t5 || $[3] !== t6) {
    t7 = {
      loop,
      align,
      dragFree: t5,
      containScroll: t6
    };
    $[0] = align;
    $[1] = loop;
    $[2] = t5;
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const options = t7;
  let t8;
  if ($[5] !== autoplay || $[6] !== prefersReducedMotion) {
    t8 = autoplay ? [Autoplay({
      ...(typeof autoplay === "object" ? autoplay : {
        delay: 5000,
        stopOnInteraction: false
      }),
      playOnInit: !prefersReducedMotion
    })] : [];
    $[5] = autoplay;
    $[6] = prefersReducedMotion;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== autoScroll || $[9] !== prefersReducedMotion) {
    t9 = autoScroll ? [AutoScroll({
      ...autoScroll,
      playOnInit: !prefersReducedMotion
    })] : [];
    $[8] = autoScroll;
    $[9] = prefersReducedMotion;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t8 || $[12] !== t9) {
    t10 = [...t8, ...t9];
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const plugins = t10;
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
  let t11;
  if ($[14] !== emblaApi) {
    t11 = () => {
      if (!emblaApi) {
        return;
      }
      return emblaApi.plugins()?.autoplay as ReturnType<typeof Autoplay> | undefined;
    };
    $[14] = emblaApi;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const getAutoplay = t11;
  let t12;
  if ($[16] !== getAutoplay) {
    t12 = () => {
      const autoplayPlugin = getAutoplay();
      autoplayPlugin?.reset?.();
    };
    $[16] = getAutoplay;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const resetAutoplay = t12;
  let t13;
  let t14;
  if ($[18] !== emblaApi || $[19] !== resetAutoplay) {
    t13 = () => {
      if (!emblaApi) {
        return {
          scrollNext: _temp,
          scrollPrev: _temp2,
          scrollTo: _temp3,
          selectedScrollSnap: _temp4,
          canScrollNext: _temp5,
          canScrollPrev: _temp6
        };
      }
      return {
        scrollNext: () => {
          emblaApi.scrollNext();
          resetAutoplay();
        },
        scrollPrev: () => {
          emblaApi.scrollPrev();
          resetAutoplay();
        },
        scrollTo: index => emblaApi.scrollTo(index),
        selectedScrollSnap: () => emblaApi.selectedScrollSnap(),
        canScrollNext: () => emblaApi.canScrollNext(),
        canScrollPrev: () => emblaApi.canScrollPrev()
      };
    };
    t14 = [emblaApi, resetAutoplay];
    $[18] = emblaApi;
    $[19] = resetAutoplay;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  useImperativeHandle(carouselRef, t13, t14);
  let t15;
  if ($[22] !== emblaApi || $[23] !== onSlideChange) {
    t15 = () => {
      if (!emblaApi) {
        return;
      }
      const index_0 = emblaApi.selectedScrollSnap();
      onSlideChange?.(index_0);
    };
    $[22] = emblaApi;
    $[23] = onSlideChange;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const onSelect = t15;
  let t16;
  if ($[25] !== emblaApi || $[26] !== onScrollMetrics) {
    t16 = () => {
      if (!emblaApi || !onScrollMetrics) {
        return;
      }
      const viewportWidth = emblaApi.rootNode().clientWidth;
      const contentWidth = emblaApi.containerNode().scrollWidth;
      onScrollMetrics({
        progress: emblaApi.scrollProgress(),
        visibleRatio: Math.min(1, viewportWidth / contentWidth)
      });
    };
    $[25] = emblaApi;
    $[26] = onScrollMetrics;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const onScroll = t16;
  let t17;
  let t18;
  if ($[28] !== emblaApi || $[29] !== onScroll || $[30] !== onSelect) {
    t17 = () => {
      if (!emblaApi) {
        return;
      }
      onSelect();
      onScroll();
      emblaApi.on("select", onSelect);
      emblaApi.on("scroll", onScroll);
      emblaApi.on("reInit", onScroll);
      return () => {
        emblaApi.off("select", onSelect);
        emblaApi.off("scroll", onScroll);
        emblaApi.off("reInit", onScroll);
      };
    };
    t18 = [emblaApi, onSelect, onScroll];
    $[28] = emblaApi;
    $[29] = onScroll;
    $[30] = onSelect;
    $[31] = t17;
    $[32] = t18;
  } else {
    t17 = $[31];
    t18 = $[32];
  }
  useEffect(t17, t18);
  let t19;
  if ($[33] !== className) {
    t19 = cn("overflow-hidden", className);
    $[33] = className;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== slideClassName) {
    t20 = cn("flex", slideClassName);
    $[35] = slideClassName;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== children || $[38] !== t20) {
    t21 = <div className={t20}>{children}</div>;
    $[37] = children;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== emblaRef || $[41] !== t19 || $[42] !== t21) {
    t22 = <div ref={emblaRef} className={t19}>{t21}</div>;
    $[40] = emblaRef;
    $[41] = t19;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  return t22;
};
HorizontalScroll.displayName = 'HorizontalScroll';
function _temp() {}
function _temp2() {}
function _temp3() {}
function _temp4() {
  return 0;
}
function _temp5() {
  return false;
}
function _temp6() {
  return false;
}